/**
 * iter214 — Route Planner dialog for a vehicle + date.
 *
 * Reuses the transport board data. User picks a depot warehouse + shift
 * start + service time; we call `/api/transport/routes/plan` which fires
 * OpenRouteService directions and returns per-stop ETAs + a polyline.
 *
 * Frontend map: react-leaflet + OSM tiles (attribution mandatory).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Path, Warning, ArrowsClockwise, Lightning } from '@phosphor-icons/react';
import { listDepots, planRoute, autoSequenceRoute, setRouteSequence } from '../lib/api';

// Leaflet default marker asset paths break with webpack — inline SVG icons instead
const DEPOT_ICON = new L.DivIcon({
  html: '<div style="background:#1d4ed8;color:white;border-radius:50%;width:22px;height:22px;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3)">D</div>',
  className: '', iconSize: [22, 22], iconAnchor: [11, 11],
});
function stopIcon(seq, breach) {
  const bg = breach ? '#dc2626' : '#059669';
  return new L.DivIcon({
    html: `<div style="background:${bg};color:white;border-radius:50%;width:22px;height:22px;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3)">${seq}</div>`,
    className: '', iconSize: [22, 22], iconAnchor: [11, 11],
  });
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 1) map.fitBounds(points, { padding: [30, 30] });
  }, [map, points]);
  return null;
}

function formatDuration(s) {
  if (!s || s < 60) return `${Math.round(s || 0)}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}
function formatDistance(m) {
  if (!m) return '0 km';
  return `${(m / 1000).toFixed(1)} km`;
}
function timeOnly(iso) {
  if (!iso) return '—';
  const dt = new Date(iso);
  return dt.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

export default function RoutePlannerDialog({ open, onClose, vehicle, plannedDate, onSequenced }) {
  const [depots, setDepots] = useState([]);
  const [depotId, setDepotId] = useState('');
  const [endDepotId, setEndDepotId] = useState('');
  const [shiftStart, setShiftStart] = useState('07:00');
  const [serviceMin, setServiceMin] = useState(10);
  const [returnDepot, setReturnDepot] = useState(true);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    listDepots().then((r) => setDepots(r.data.depots || []));
  }, [open]);

  const compute = async () => {
    if (!vehicle) return;
    setLoading(true);
    setPlan(null);
    try {
      const shiftISO = `${plannedDate}T${shiftStart}:00`;
      const r = await planRoute({
        vehicle_id: vehicle.id,
        planned_date: plannedDate,
        start_depot_warehouse_id: depotId || null,
        end_depot_warehouse_id: endDepotId || null,
        return_to_depot: returnDepot,
        shift_start: shiftISO,
        service_time_minutes: Number(serviceMin) || 0,
      });
      setPlan(r.data);
      if (r.data.unresolved?.length > 0) {
        toast.warning(`${r.data.unresolved.length} stop(s) had unresolvable addresses`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Route planning failed');
    } finally {
      setLoading(false);
    }
  };

  const doAutoSequence = async (objective = 'distance') => {
    setBusy(true);
    try {
      const r = await autoSequenceRoute({
        vehicle_id: vehicle.id,
        planned_date: plannedDate,
        start_depot_warehouse_id: depotId || null,
        end_depot_warehouse_id: endDepotId || null,
        objective,
        return_to_depot: returnDepot,
        apply: true,
      });
      const s = r.data.savings || {};
      // Only show a metric in the toast if it's actually a positive saving —
      // when the user asks for 'fastest', distance can wobble +/- a bit and
      // vice-versa; hiding negatives keeps the message honest.
      const kmSaved = (s.distance_m || 0) > 0 ? (s.distance_m / 1000).toFixed(1) : null;
      const minsSaved = (s.duration_s || 0) > 0 ? Math.round(s.duration_s / 60) : null;
      const bits = [];
      if (kmSaved) bits.push(`${kmSaved} km`);
      if (minsSaved) bits.push(`${minsSaved} min`);
      if (objective === 'distance' && s.distance_pct != null && s.distance_pct > 0) bits.push(`${s.distance_pct}%`);
      if (objective === 'duration' && s.duration_pct != null && s.duration_pct > 0) bits.push(`${s.duration_pct}%`);
      toast.success(
        bits.length
          ? `Optimised ${r.data.sequence.length} stops · saved ${bits.join(' · ')}`
          : `Optimised ${r.data.sequence.length} stops (already optimal)`
      );
      await compute();
      onSequenced?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-sequence failed');
    } finally {
      setBusy(false);
    }
  };

  const moveStop = async (fromIdx, toIdx) => {
    // Reorder locally, then persist via /set-sequence
    const stops = (plan?.stops || []).filter((s) => s.kind === 'stop');
    if (fromIdx < 0 || fromIdx >= stops.length || toIdx < 0 || toIdx >= stops.length) return;
    const next = [...stops];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    try {
      await setRouteSequence(next.map((s) => s.job_id));
      toast.success('Stops reordered');
      await compute();
      onSequenced?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reorder failed');
    }
  };

  const mapPoints = useMemo(() => (plan?.stops || []).map((s) => [s.lat, s.lon]), [plan]);
  const polyline = useMemo(() => plan?.geometry_latlon || [], [plan]);
  const stopsOnly = useMemo(() => (plan?.stops || []).filter((s) => s.kind === 'stop'), [plan]);
  const anyBreach = stopsOnly.some((s) => s.window_breach);
  // iter232 — composite key so multi-stop milk runs (same job_id, different
  // stop_sequence) each get their own index + marker.
  const stopKey = (s) => `${s.job_id}:${s.stop_sequence ?? ''}`;
  const stopIndex = (s) => stopsOnly.findIndex((x) => stopKey(x) === stopKey(s));

  if (!vehicle) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col" data-testid="route-planner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Path size={18} className="text-brand" weight="bold" />
            Route Planner — {vehicle.registration}
          </DialogTitle>
          <DialogDescription>
            {plannedDate} · profile <span className="font-mono">{vehicle.route_profile || 'driving-hgv'}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-5 gap-2 items-end">
          <div>
            <Label className="text-xs">Start depot</Label>
            <select
              className="w-full h-9 rounded border border-border px-2 text-sm"
              value={depotId} onChange={(e) => setDepotId(e.target.value)}
              data-testid="route-depot-select"
            >
              <option value="">— None (use first stop) —</option>
              {depots.map((d) => (<option key={d.id} value={d.id}>{d.name}{d.address ? ` · ${d.address}` : ''}</option>))}
            </select>
          </div>
          <div>
            <Label className="text-xs">End depot (multi-depot)</Label>
            <select
              className="w-full h-9 rounded border border-border px-2 text-sm"
              value={endDepotId} onChange={(e) => setEndDepotId(e.target.value)}
              data-testid="route-end-depot-select"
              title="Leave empty to loop back to the start depot (single-depot mode)"
            >
              <option value="">— Same as start (loop) —</option>
              {depots.map((d) => (<option key={d.id} value={d.id}>{d.name}{d.address ? ` · ${d.address}` : ''}</option>))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Shift start</Label>
            <Input type="time" value={shiftStart} onChange={(e) => setShiftStart(e.target.value)} data-testid="route-shift-start" />
          </div>
          <div>
            <Label className="text-xs">Service min / stop</Label>
            <Input type="number" min="0" max="120" value={serviceMin} onChange={(e) => setServiceMin(e.target.value)} data-testid="route-service-min" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs flex items-center gap-1">
              <input type="checkbox" checked={returnDepot} onChange={(e) => setReturnDepot(e.target.checked)} data-testid="route-return-depot" />
              Return to depot
            </label>
          </div>
        </div>

        <div className="flex gap-2 pt-2 flex-wrap items-center">
          <Button size="sm" onClick={compute} disabled={loading} data-testid="route-compute-btn">
            {loading ? 'Routing…' : 'Compute route'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => doAutoSequence('distance')} disabled={busy || loading} data-testid="route-auto-sequence-btn" title="Reorder stops for the fewest kilometres (nearest-neighbour seed + 2-opt polish)">
            <Lightning size={12} className="mr-1" weight="fill" /> Optimise · fewest km
          </Button>
          <Button size="sm" variant="ghost" onClick={() => doAutoSequence('duration')} disabled={busy || loading} data-testid="route-auto-sequence-fastest-btn" title="Reorder stops for the fastest drive time">
            <Lightning size={12} className="mr-1" /> Optimise · fastest
          </Button>
          {plan && (
            <div className="ml-auto text-xs flex items-center gap-3">
              <Badge variant="outline" className="text-xs" data-testid="route-total-distance">
                <Path size={12} className="mr-1" /> {formatDistance(plan.distance_m)}
              </Badge>
              <Badge variant="outline" className="text-xs" data-testid="route-total-duration">
                {formatDuration(plan.duration_s)} drive
              </Badge>
              <Badge variant="outline" className="text-xs">
                profile {plan.profile}
              </Badge>
              {anyBreach && (
                <Badge className="bg-red-100 text-red-800 border-red-300 text-xs">
                  <Warning size={12} className="mr-1" /> Window breach
                </Badge>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1 overflow-hidden pt-3">
          {/* Stop list */}
          <div className="space-y-1 overflow-y-auto pr-1" data-testid="route-stops-list">
            {!plan && <div className="text-xs text-muted-foreground italic p-2">Click 'Compute route' to fetch distance + ETA from OpenRouteService.</div>}
            {plan?.stops.map((s, i) => (
              <Card key={i} className={`p-2 text-xs ${s.window_breach ? 'border-red-300' : ''}`} data-testid={`route-stop-${i}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${s.kind === 'depot' ? 'bg-blue-700' : s.kind === 'return' ? 'bg-slate-500' : s.window_breach ? 'bg-red-600' : 'bg-emerald-600'}`}>
                    {s.kind === 'depot' ? 'D' : s.kind === 'return' ? 'R' : stopIndex(s) + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold flex items-center gap-1">
                      {s.customer_name || s.label}
                      {s.multi_stop && (
                        <span className="text-[9px] uppercase tracking-widest text-indigo-600 bg-indigo-50 border border-indigo-200 px-1 rounded">
                          {s.job_number} · stop {s.stop_sequence}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate" title={s.address}>{s.address || '—'}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] text-muted-foreground">arr</div>
                    <div className="font-mono">{timeOnly(s.arrival_eta)}</div>
                  </div>
                  {s.kind === 'stop' && !s.multi_stop && (
                    <div className="flex flex-col gap-0.5 ml-1">
                      <Button size="xs" variant="ghost" className="h-4 p-0 text-[9px]"
                              onClick={() => moveStop(stopIndex(s), stopIndex(s) - 1)}
                              data-testid={`route-stop-up-${s.job_id}`}>▲</Button>
                      <Button size="xs" variant="ghost" className="h-4 p-0 text-[9px]"
                              onClick={() => moveStop(stopIndex(s), stopIndex(s) + 1)}
                              data-testid={`route-stop-down-${s.job_id}`}>▼</Button>
                    </div>
                  )}
                </div>
                {s.window_breach && (
                  <div className="text-[10px] text-red-700 mt-1 flex items-center gap-1">
                    <Warning size={10} /> {s.window_breach}
                  </div>
                )}
                {s.window_start && (
                  <div className="text-[10px] text-muted-foreground mt-0.5">window {s.window_start}–{s.window_end || '—'}</div>
                )}
              </Card>
            ))}
            {plan?.unresolved?.length > 0 && (
              <Card className="p-2 text-xs border-amber-300 bg-amber-50" data-testid="route-unresolved">
                <div className="font-semibold text-amber-800">Unresolved addresses ({plan.unresolved.length})</div>
                {plan.unresolved.map((u) => <div key={u.job_id} className="text-[10px]">{u.job_number} — {u.reason}</div>)}
              </Card>
            )}
          </div>
          {/* Map */}
          <div className="border border-border rounded overflow-hidden" style={{ minHeight: 400 }} data-testid="route-map">
            {polyline.length > 0 ? (
              <MapContainer center={polyline[0]} zoom={12} style={{ height: '100%', minHeight: 400, width: '100%' }}>
                <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                           attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
                <FitBounds points={mapPoints} />
                <Polyline positions={polyline} pathOptions={{ color: '#2563eb', weight: 4 }} />
                {plan.stops.map((s, i) => (
                  <Marker key={i} position={[s.lat, s.lon]}
                          icon={s.kind === 'depot' || s.kind === 'return' ? DEPOT_ICON : stopIcon(stopIndex(s) + 1, !!s.window_breach)}>
                    <Popup>
                      <div className="text-xs">
                        <div className="font-semibold">{s.customer_name || s.label}</div>
                        <div>{s.address || '—'}</div>
                        <div>ETA {timeOnly(s.arrival_eta)}</div>
                        {s.window_breach && <div className="text-red-700 font-medium">{s.window_breach}</div>}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-muted-foreground p-6 text-center">
                <div>
                  <MapPin size={32} className="mx-auto mb-2 text-slate-300" />
                  Map appears here once the route is computed.
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
